import { format } from 'date-fns';
import { useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';
import { Box, HStack, Text } from '../../../components/primitives';
import { useTranslation } from '../../../contexts/TranslationContext';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';
import type { CalendarEvent } from '../../../types/calendar';
import {
  EventSourceIndicator,
  useEventSourceAccessibilityLabel,
} from '../indicators/EventSourceIndicator';
import { getEventAppearance } from '../utils/calendarEventAppearance';
import { EventPreviewHost } from './EventPreviewHost';

interface MonthEventChipProps {
  event: CalendarEvent;
}

// Tap-to-preview, not tap-to-edit. EventCard's day-list row taps open the
// edit form and reserve hover(web)/long-press(native) for the preview — but
// a month-grid chip is too small and dense for that split. Here a single tap
// opens the SAME EventPreviewHost the day list shows on hover/long-press,
// reused completely unchanged (only the trigger differs — local press state
// instead of useEventPreview's hover/long-press wiring). There is no edit
// affordance from the chip itself; drilling into Day view via the cell's own
// tap is where a rep reaches the full event.
export function MonthEventChip({ event }: Readonly<MonthEventChipProps>) {
  const [previewing, setPreviewing] = useState(false);
  const { t } = useTranslation();
  const sourceLabel = useEventSourceAccessibilityLabel(event.source);

  // A month cell has no room for a status Badge, so the chip carries only the
  // two signals that cost no width: the strike and the muted text token. The
  // word itself goes into the accessible label below.
  const appearance = getEventAppearance(event.source, event.status);
  const statusLabel = appearance.statusLabelKey ? t(appearance.statusLabelKey) : null;

  // The indicator is icon-only here and its accessible phrase is folded into
  // this Pressable's own label rather than left on the nested Badge: a parent
  // accessibilityLabel overrides its children's, so an indicator inside would
  // be visible and silent. Cell width is the constraint — the chip shrinks to
  // it (a fixed title width used to spill over the neighbouring day number).
  const accessibilityLabel = [
    event.title,
    format(new Date(event.startsAt), 'h:mm a'),
    sourceLabel,
    statusLabel,
  ]
    .filter(Boolean)
    .join(', ');

  return (
    <View style={{ position: 'relative' }}>
      <Pressable
        testID={`calendar_month_chip_${event.id}`}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel}
        onPress={(e) => {
          // Stops the tap from also reaching the day cell's own Pressable
          // (which drills into Day view) — same defensive pattern as
          // EventCard's inline Cancel button.
          e?.stopPropagation?.();
          setPreviewing((prev) => !prev);
        }}
      >
        <HStack spacing="xxs" align="center" style={{ flexShrink: 1, minWidth: 0 }}>
          <EventSourceIndicator
            source={event.source}
            size="sm"
            iconOnly
            testID={`calendar_month_chip_source_${event.id}`}
          />
          {/* Tinted by source, exactly as the day and week blocks are — the
              icon alone was the only source signal here, and an icon inside a
              ~100px chip is easy to miss. */}
          <Box
            testID={`calendar_month_chip_surface_${event.id}`}
            style={[
              styles.chip,
              { backgroundColor: appearance.fill, borderColor: appearance.borderColor },
            ]}
          >
            <Text
              testID={`calendar_month_chip_title_${event.id}`}
              variant="caption"
              numberOfLines={1}
              color={appearance.titleColor}
              style={[
                { fontWeight: '500', flexShrink: 1 },
                appearance.strikeThrough && { textDecorationLine: 'line-through' },
              ]}
            >
              {event.title}
            </Text>
          </Box>
        </HStack>
      </Pressable>

      <EventPreviewHost event={previewing ? event : null} onDismiss={() => setPreviewing(false)} />
    </View>
  );
}

const styles = StyleSheet.create({
  chip: {
    flexShrink: 1,
    minWidth: 0,
    borderWidth: 1,
    borderRadius: tokens.radius.sm,
    paddingVertical: 1,
    paddingHorizontal: tokens.spacing.xs,
  },
});
